import { Link, useSearchParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import Seo from "@/components/seo/Seo";
import PreorderForm from "@/components/shop/PreorderForm";
import OilFill from "@/components/motion/OilFill";
import ShimmeringText from "@/components/motion/ShimmeringText";
import CountingNumber from "@/components/motion/CountingNumber";
import { useCatalog, usePreorderSummary, isSoldOut } from "@/lib/shop";
import { BRAND, LAB, PURITY_REPORT } from "@/content/brand";

const STEPS = [
  ["Reserve", "Choose your jar and quantity. We take your details, not your money."],
  ["We press and test", "The next batch is pressed from our own copra and sent to an independent lab before a single jar leaves."],
  ["Pay, then we ship", "Once your batch passes, we send a secure payment link on WhatsApp or email. Pay, and it is dispatched in 24 to 48 hours."],
];

const FAQ = [
  ["Do I pay anything now?", "No. A pre-order only reserves your oil. We ask for payment after we confirm your batch has been pressed and tested, through a secure Razorpay link."],
  ["When will my oil arrive?", "We confirm a date when your batch is ready, not before. We would rather give you a real date than a hopeful one. Delivery takes 3 to 7 business days after payment."],
  ["Will the price change?", "You pay the price shown on the site when we send your payment link. If it has changed since you reserved, you will see the new price before paying and can cancel."],
  ["Can I cancel?", "Yes, any time before you pay. Reply to our message or write to us. Nothing is owed."],
  ["Why does it sell out?", "We press only what our family farms in Tiptur and on the Uttara Kannada coast grow, in small batches, and every batch waits for its lab result. We do not top up with bought oil."],
];

/**
 * Sold out, pre-order page. Everything on it is true: no invented demand,
 * no countdown. The litres counter is the real total of open pre-orders and
 * only appears once it passes the threshold set in Admin > Settings.
 */
export default function PreOrder() {
  const [search] = useSearchParams();
  const catalog = useCatalog() || [];
  const [summary, setSummary] = usePreorderSummary();
  const initialVariant = search.get("variant") || "";
  const soldOutProducts = catalog.filter(isSoldOut);
  const heading = soldOutProducts.length && soldOutProducts.length === catalog.filter((p) => p.kind !== "gift").length
    ? "Our liquid gold is sold out."
    : "This batch is sold out.";

  return (
    <>
      <Seo title="Sold Out: Pre-order Karijeeva Cold Pressed Coconut Oil" description="Karijeeva cold pressed coconut oil is sold out. Reserve the next small batch from our family farms in Karnataka. No payment until your order is confirmed." noindex />

      <section className="relative bg-brand-obsidian text-brand-bone overflow-hidden" data-testid="preorder-hero">
        <div aria-hidden="true" className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,rgba(243,227,161,0.22),transparent_65%)]" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-[1.3fr_1fr] gap-12 items-center">
          <div>
            <p className="font-body text-[15px] font-semibold"><ShimmeringText text="Karijeeva Liquid Gold" color="#F3E3A1" shimmeringColor="#FFFFFF" /></p>
            <h1 className="mt-3 font-display text-white text-[clamp(2.6rem,7vw,5.25rem)] leading-[0.98] tracking-display">{heading}</h1>
            <p className="mt-6 max-w-xl font-body text-[18px] sm:text-[20px] leading-snug text-brand-bone/75">
              We press only what our own coconuts give us, in small batches, and nothing leaves until it passes an independent lab test. Reserve yours from the next batch now. <strong className="text-white">You pay only after we confirm it.</strong>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
              <a href="#reserve" className="inline-flex items-center rounded-control bg-[#F3E3A1] hover:bg-[#EBD483] text-brand-ink font-body text-[15px] font-medium px-6 py-3 transition-colors" data-testid="preorder-cta">
                Reserve my oil
              </a>
              <Link to="/lab-reports" className="inline-flex items-center gap-1 font-body text-[15px] text-brand-bone hover:underline">
                How we test every batch <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            {summary?.show && (
              <div className="mt-10 inline-flex items-baseline gap-3 rounded-2xl border border-white/10 bg-white/5 px-5 py-4" data-testid="preorder-counter">
                <span className="font-display text-white text-[40px] tracking-heading"><CountingNumber number={summary.litres} decimals={summary.litres % 1 ? 1 : 0} /></span>
                <span className="font-body text-[15px] text-brand-bone/75">litres already reserved from the next batch</span>
              </div>
            )}
          </div>
          <div className="flex justify-center">
            <OilFill level={0.9} label="A jar of Karijeeva filling with golden oil" className="h-72 sm:h-96 w-auto drop-shadow-[0_30px_60px_rgba(243,227,161,0.25)]" />
          </div>
        </div>
      </section>

      <section className="bg-brand-parchment py-20" aria-labelledby="how">
        <div className="max-w-5xl mx-auto px-6">
          <h2 id="how" className="font-display text-[clamp(2rem,5vw,3rem)] leading-[1.04] tracking-display text-brand-ink">How a pre-order works.</h2>
          <ol className="mt-10 grid md:grid-cols-3 gap-4">
            {STEPS.map(([t, b], i) => (
              <li key={t} className="rounded-3xl bg-white p-7">
                <p className="font-body text-[13px] font-semibold text-brand-gold">Step {i + 1}</p>
                <p className="mt-2 font-display text-[22px] tracking-[-0.02em] text-brand-ink">{t}</p>
                <p className="mt-2 font-body text-[15px] leading-relaxed text-ink-muted">{b}</p>
              </li>
            ))}
          </ol>
          <p className="mt-8 font-body text-[14px] text-ink-muted">
            Latest batch report: met the FSSAI coconut oil standard on all {PURITY_REPORT.rows.length} purity tests at {LAB.name}, {LAB.city}. FSSAI licence {BRAND.fssai}.
          </p>
        </div>
      </section>

      <section id="reserve" className="bg-brand-parchment-soft py-20 scroll-mt-16">
        <div className="max-w-5xl mx-auto px-6 grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div>
            <h2 className="font-display text-[clamp(2rem,5vw,3rem)] leading-[1.04] tracking-display text-brand-ink">Reserve yours.</h2>
            <p className="mt-4 font-body text-[17px] leading-relaxed text-ink-muted">Two minutes, no card, no account. We will write to you when your batch is ready.</p>
            <dl className="mt-8 divide-y divide-black/10 border-y border-black/10">
              {FAQ.map(([q, a]) => (
                <div key={q} className="py-4">
                  <dt className="font-display text-[17px] text-brand-ink">{q}</dt>
                  <dd className="mt-1 font-body text-[15px] leading-relaxed text-ink-muted">{a}</dd>
                </div>
              ))}
            </dl>
          </div>
          {catalog.length ? (
            <PreorderForm products={catalog.filter((p) => p.kind !== "gift")} initialVariantId={initialVariant} onDone={(d) => d?.summary && setSummary(d.summary)} />
          ) : (
            <p className="font-body text-ink-muted">Loading</p>
          )}
        </div>
      </section>
    </>
  );
}
